import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';
import { usePushNotifications } from '../hooks/usePushNotifications';

import {
    updatePassword,
    updateEmail,
    signOut,
    reauthenticateWithCredential,
    EmailAuthProvider
} from 'firebase/auth';
import { ref, get, query, orderByChild, equalTo, update } from 'firebase/database';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch, apiGet, apiPost } from '../utils/api';

interface UserData {
    name: string;
    email: string;
    phone: string;
    plan: string;
    createdAt: number;
    lastLogin?: number;
    notificationPreferences?: {
        emailNotifications: boolean;
        smsNotifications: boolean;
        marketingEmails: boolean;
    };
}

export default function Account() {
    const navigate = useNavigate();
    const { isDark, toggle } = useTheme();
    const [user, setUser] = useState<any>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [bots, setBots] = useState<any[]>([]);
  const brandName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
    // Form states
    const [displayName, setDisplayName] = useState('');
    const [phone, setPhone] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');

    // Notification states
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [smsNotifications, setSmsNotifications] = useState(true);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const push = usePushNotifications();

    // WhatsApp phone verification
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpCooldown, setOtpCooldown] = useState(0);

    // UI states
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [verificationSent, setVerificationSent] = useState(false);

    // Company details
    const [coName, setCoName]         = useState('');
    const [coIndustry, setCoIndustry] = useState('');
    const [coWebsite, setCoWebsite]   = useState('');
    const [coEmail, setCoEmail]       = useState('');
    const [coPhone, setCoPhone]       = useState('');

    // Integrations (external partner API keys, e.g. TIRONGE)
    const [integrationKeys, setIntegrationKeys] = useState<{ hash: string; label: string; createdAt: number }[]>([]);
    const [newIntegrationLabel, setNewIntegrationLabel] = useState('TIRONGE');
    const [generatedIntegrationKey, setGeneratedIntegrationKey] = useState<string | null>(null);
    const [integrationsLoading, setIntegrationsLoading] = useState(false);

    // Load user data
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            if (!currentUser) {
                navigate('/login');
                return;
            }

            setUser(currentUser);

            // Load user data from database
            const userSnapshot = await get(ref(db, `users/${currentUser.uid}`));
            const data = userSnapshot.val();
            setUserData(data);

            setDisplayName(data?.name || currentUser.displayName || '');
            setPhone(data?.phone || '');
            setPhoneVerified(!!data?.phoneVerified);
            setNewEmail(currentUser.email || '');
            setEmailNotifications(data?.notificationPreferences?.emailNotifications ?? true);
            setSmsNotifications(data?.notificationPreferences?.smsNotifications ?? true);
            setMarketingEmails(data?.notificationPreferences?.marketingEmails ?? false);
            setCoName(data?.company?.name || '');
            setCoIndustry(data?.company?.industry || '');
            setCoWebsite(data?.company?.website || '');
            setCoEmail(data?.company?.email || '');
            setCoPhone(data?.company?.phone || '');

            // Load user's bots
            const botsRef = ref(db, 'bots');
            const botsQuery = query(botsRef, orderByChild('userId'), equalTo(currentUser.uid));
            const botsSnapshot = await get(botsQuery);
            const botsList: any[] = [];
            botsSnapshot.forEach((child) => {
                botsList.push({ id: child.key, ...child.val() });
            });
            setBots(botsList);

            setLoading(false);
        });

        return unsubscribe;
    }, [navigate]);

    // Integrations — lazy-load when the tab is opened
    const loadIntegrationKeys = async () => {
        setIntegrationsLoading(true);
        try {
            const data = await apiGet('/api/integrations');
            setIntegrationKeys(data.keys || []);
        } catch (err) {
            console.error('loadIntegrationKeys error:', err);
        } finally {
            setIntegrationsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'integrations') loadIntegrationKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleGenerateIntegrationKey = async () => {
        try {
            const data = await apiPost('/api/integrations/generate', { label: newIntegrationLabel.trim() || 'Integration' });
            setGeneratedIntegrationKey(data.key);
            await loadIntegrationKeys();
        } catch (err) {
            setMessage({ type: 'error', text: 'Could not generate an integration key. Please try again.' });
        }
    };

    const handleRevokeIntegrationKey = async (hash: string) => {
        if (!window.confirm('Revoke this key? Anything using it (e.g. TIRONGE) will stop working until you connect a new one.')) return;
        try {
            await apiFetch(`/api/integrations/${hash}`, { method: 'DELETE' });
            await loadIntegrationKeys();
        } catch (err) {
            setMessage({ type: 'error', text: 'Could not revoke that key.' });
        }
    };

    // Update profile (name & phone)
    const handleUpdateProfile = async () => {
        setMessage(null);
        setLoading(true);

        try {
            // Update in Firebase Auth
            if (user && displayName !== user.displayName) {
                const { updateProfile } = await import('firebase/auth');
                await updateProfile(user, { displayName: displayName });
            }

            // Editing the phone number here (as opposed to going through the
            // verify-code flow below) can't be trusted as still pointing at a
            // WhatsApp number the owner actually controls, so it drops back
            // to unverified until they verify the new number.
            const phoneChanged = phone !== (userData?.phone || '');
            await update(ref(db, `users/${user.uid}`), {
                name: displayName,
                phone: phone,
                ...(phoneChanged ? { phoneVerified: false } : {}),
                updatedAt: Date.now()
            });
            if (phoneChanged) {
                setPhoneVerified(false);
                setOtpSent(false);
                setOtpCode('');
            }

            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Live countdown for the resend-code cooldown
    useEffect(() => {
        if (otpCooldown <= 0) return;
        const t = setInterval(() => setOtpCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [otpCooldown]);

    const handleSendOtp = async () => {
        setOtpError('');
        if (!phone.trim()) { setOtpError('Enter your WhatsApp number first'); return; }
        setOtpSending(true);
        try {
            const res = await apiFetch('/api/account/phone/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
            const data = await res.json();
            if (!data.success) { setOtpError(data.error || 'Could not send the code'); return; }
            setOtpSent(true);
            setOtpCooldown(60);
        } catch {
            setOtpError('Could not reach the server. Please try again.');
        } finally {
            setOtpSending(false);
        }
    };

    const handleVerifyOtp = async () => {
        setOtpError('');
        if (!otpCode.trim()) { setOtpError('Enter the code you received on WhatsApp'); return; }
        setOtpVerifying(true);
        try {
            const res = await apiFetch('/api/account/phone/verify-otp', { method: 'POST', body: JSON.stringify({ code: otpCode }) });
            const data = await res.json();
            if (!data.success) { setOtpError(data.error || 'Incorrect code'); return; }
            setPhoneVerified(true);
            setOtpSent(false);
            setOtpCode('');
            if (data.phone) setPhone(data.phone);
            setMessage({ type: 'success', text: 'WhatsApp number verified!' });
        } catch {
            setOtpError('Could not reach the server. Please try again.');
        } finally {
            setOtpVerifying(false);
        }
    };

    // Change password
    const handleChangePassword = async () => {
        setMessage(null);

        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match!' });
            return;
        }

        if (newPassword.length < 6) {
            setMessage({ type: 'error', text: 'Password must be at least 6 characters!' });
            return;
        }

        setLoading(true);

        try {
            // Re-authenticate user first
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);

            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setMessage({ type: 'success', text: 'Password changed successfully!' });
        } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
                setMessage({ type: 'error', text: 'Current password is incorrect!' });
            } else {
                setMessage({ type: 'error', text: error.message });
            }
        } finally {
            setLoading(false);
        }
    };

    // Change email
    const handleChangeEmail = async () => {
        setMessage(null);

        if (!newEmail || newEmail === user.email) {
            setMessage({ type: 'error', text: 'Please enter a different email address!' });
            return;
        }

        setLoading(true);

        try {
            // Re-authenticate user first
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update email
            await updateEmail(user, newEmail);

            // Update in database
            await update(ref(db, `users/${user.uid}`), {
                email: newEmail,
                updatedAt: Date.now()
            });

            setMessage({ type: 'success', text: 'Email changed successfully! Please verify your new email.' });
            setVerificationSent(false);
        } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
                setMessage({ type: 'error', text: 'Password is incorrect!' });
            } else if (error.code === 'auth/requires-recent-login') {
                setMessage({ type: 'error', text: 'Please log out and log back in before changing email.' });
            } else {
                setMessage({ type: 'error', text: error.message });
            }
        } finally {
            setLoading(false);
        }
    };

    // Send email verification
    const handleSendVerification = async () => {
        setMessage(null);
        setLoading(true);

        try {
            // Sent via our own SMTP (server-side), not Firebase Auth's built-in
            // email delivery — see /api/auth/request-email-verification.
            await apiPost('/api/auth/request-email-verification', {});
            setVerificationSent(true);
            setMessage({ type: 'success', text: 'Verification email sent! Please check your inbox.' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Update notification preferences
    const handleUpdateNotifications = async () => {
        setMessage(null);
        setLoading(true);

        try {
            await update(ref(db, `users/${user.uid}`), {
                notificationPreferences: {
                    emailNotifications,
                    smsNotifications,
                    marketingEmails
                },
                updatedAt: Date.now()
            });

            setMessage({ type: 'success', text: 'Notification preferences updated!' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Update company details
    const handleUpdateCompany = async () => {
        setMessage(null);
        setLoading(true);
        try {
            await update(ref(db, `users/${user.uid}`), {
                company: { name: coName, industry: coIndustry, website: coWebsite, email: coEmail, phone: coPhone, updatedAt: Date.now() },
                updatedAt: Date.now()
            });
            setMessage({ type: 'success', text: 'Company details updated!' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    // Delete account and all associated data
    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
            setMessage({ type: 'error', text: 'Please type "DELETE MY ACCOUNT" to confirm.' });
            return;
        }

        setLoading(true);

        try {
            // Re-authenticate user first (required before sensitive operations)
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Call the server-side deletion endpoint — it removes all DB data
            // (bots, campaigns, contacts, invoices) and deletes the Auth account server-side.
            const res = await apiFetch('/api/account', { method: 'DELETE' });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Deletion failed');

            // Sign out locally (Auth account already deleted server-side)
            await signOut(auth);

            // Redirect to home
            navigate('/');
        } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
                setMessage({ type: 'error', text: 'Password is incorrect!' });
            } else if (error.code === 'auth/requires-recent-login') {
                setMessage({ type: 'error', text: 'Please log out and log back in before deleting your account.' });
            } else {
                setMessage({ type: 'error', text: error.message });
            }
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/');
    };

    if (loading && !userData) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
    }

    return (

 <>
      <SEO
        title="WhatsApp Bot Platform | Automate Customer Communication"
        description={`${brandName} helps Zimbabwean businesses automate WhatsApp communication, boost sales, and provide 24/7 customer support. Start your free trial today.`}
        keywords="WhatsApp business bot, Zimbabwe automation, customer service chatbot, AI messaging, business growth"
        type="website"
        noindex
      />
        <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--bg-page)' }}>
            {/* Sidebar */}
            <div style={{
                width: sidebarCollapsed ? '80px' : '280px',
                background: 'var(--bg-sidebar)',
                color: 'var(--text-primary)',
                borderRight: '1px solid var(--border)',
                transition: 'width 0.3s',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    style={{
                        position: 'absolute',
                        right: '-12px',
                        top: '20px',
                        width: '24px',
                        height: '24px',
                        background: '#3b82f6',
                        border: 'none',
                        borderRadius: '12px',
                        color: 'white',
                        cursor: 'pointer',
                        zIndex: 10
                    }}
                >
                    {sidebarCollapsed ? '→' : '←'}
                </button>

                <div style={{ padding: sidebarCollapsed ? '20px 0' : '24px', borderBottom: '1px solid var(--border)' }}>
                    {!sidebarCollapsed && (
                        <>
                            <img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={brandName} style={{ height: '68px', display: 'block', maxWidth: '130px', objectFit: 'contain', marginBottom: '4px' }} />
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>WhatsApp Bot Platform</p>
                        </>
                    )}
                    {sidebarCollapsed && <div style={{ textAlign: 'center' }}><img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={brandName} style={{ height: '68px', objectFit: 'contain' }} /></div>}
                </div>

                <nav style={{ flex: 1, padding: sidebarCollapsed ? '16px 0' : '24px' }}>
                    <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: sidebarCollapsed ? '12px 0' : '10px 16px', borderRadius: '8px', cursor: 'pointer', marginBottom: '4px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                            <span style={{ fontSize: '20px' }}>📊</span>
                            {!sidebarCollapsed && <span>Dashboard</span>}
                        </div>
                    </Link>
                    <Link to="/billing" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: sidebarCollapsed ? '12px 0' : '10px 16px', borderRadius: '8px', cursor: 'pointer', marginBottom: '4px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                            <span style={{ fontSize: '20px' }}>💳</span>
                            {!sidebarCollapsed && <span>Billing</span>}
                        </div>
                    </Link>
                    <div onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: sidebarCollapsed ? '12px 0' : '10px 16px', borderRadius: '8px', cursor: 'pointer', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                        <span style={{ fontSize: '20px' }}>🚪</span>
                        {!sidebarCollapsed && <span>Logout</span>}
                    </div>
                </nav>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    {/* Header */}
                    <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>Account Settings</h1>
                            <p style={{ color: '#6b7280' }}>Manage your profile, security, and preferences</p>
                        </div>
                        {/* Theme toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--bg-element)', border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer' }} onClick={toggle} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', userSelect: 'none' }}>{isDark ? '🌙 Dark' : '☀️ Light'}</span>
                            <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: isDark ? 'var(--bg-page)' : '#5B4FE8', border: `1px solid ${isDark ? 'var(--border)' : '#4338ca'}`, position: 'relative', transition: 'background 0.25s', flexShrink: 0 }}>
                                <div style={{ position: 'absolute', top: '2px', left: isDark ? '2px' : '16px', width: '14px', height: '14px', borderRadius: '50%', background: isDark ? 'var(--text-faint)' : 'white', transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                            </div>
                        </div>
                    </div>

                    {/* Email Verification Banner */}
                    {user && !user.emailVerified && (
                        <div style={{ background: '#fef3c7', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <span style={{ fontSize: '20px', marginRight: '8px' }}>⚠️</span>
                                <span style={{ fontWeight: '500' }}>Your email is not verified!</span>
                                <span style={{ fontSize: '13px', color: '#92400e', marginLeft: '8px' }}>Some features may be limited.</span>
                            </div>
                            <button
                                onClick={handleSendVerification}
                                disabled={verificationSent}
                                style={{ padding: '8px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
                            >
                                {verificationSent ? 'Verification Sent!' : 'Verify Email'}
                            </button>
                        </div>
                    )}

                    {/* Message Display */}
                    {message && (
                        <div style={{
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '24px',
                            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                            color: message.type === 'success' ? '#065f46' : '#991b1b',
                            border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`
                        }}>
                            {message.text}
                        </div>
                    )}

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '0', flexWrap: 'wrap' }}>
                        {[
                            { id: 'profile',       label: '👤 Profile' },
                            { id: 'company',       label: '🏢 Company' },
                            { id: 'security',      label: '🔒 Security' },
                            { id: 'notifications', label: '🔔 Notifications' },
                            { id: 'integrations',  label: '🔌 Integrations' },
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                style={{
                                    padding: '10px 20px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: activeTab === t.id ? '2px solid #059669' : '2px solid transparent',
                                    color: activeTab === t.id ? '#059669' : '#6b7280',
                                    cursor: 'pointer',
                                    fontWeight: activeTab === t.id ? '600' : '400',
                                    fontSize: '14px'
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>Profile Information</h2>

                            {/* Bot summary */}
                            {bots.length > 0 && (
                                <div style={{ marginBottom: '24px', padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#065f46', marginBottom: '10px' }}>Your Bots</div>
                                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                        {[
                                            { label: 'Total', value: bots.length, icon: '🤖' },
                                            { label: 'WhatsApp', value: bots.filter(b => b.botType === 'whatsapp').length, icon: '💬' },
                                            { label: 'Facebook', value: bots.filter(b => b.botType === 'facebook').length, icon: '📘' },
                                            { label: 'Active', value: bots.filter(b => b.status === 'active').length, icon: '🟢' },
                                        ].map(stat => (
                                            <div key={stat.label} style={{ textAlign: 'center', minWidth: '60px' }}>
                                                <div style={{ fontSize: '22px' }}>{stat.icon}</div>
                                                <div style={{ fontSize: '20px', fontWeight: 700, color: '#065f46' }}>{stat.value}</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Plan badge */}
                            {userData?.plan && (
                                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Current plan:</span>
                                    <span style={{ padding: '3px 10px', background: '#059669', color: 'white', borderRadius: '20px', fontSize: '12px', fontWeight: 700, textTransform: 'capitalize' }}>
                                        {userData.plan}
                                    </span>
                                    <Link to="/billing" style={{ fontSize: '12px', color: '#059669' }}>Upgrade →</Link>
                                </div>
                            )}

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Display Name</label>
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    placeholder="Your name"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500', marginBottom: '8px' }}>
                                    WhatsApp Number
                                    {phoneVerified ? (
                                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669' }}>✓ Verified</span>
                                    ) : (
                                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#d97706' }}>⚠ Not verified</span>
                                    )}
                                </label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => { setPhone(e.target.value); setOtpSent(false); setOtpError(''); }}
                                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    placeholder="+263712345678"
                                />
                                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                    We send draft-reply reviews, handoff alerts and other account notifications here — it has to be verified to receive them.
                                </p>

                                {!phoneVerified && (
                                    <div style={{ marginTop: '10px', padding: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                                        {!otpSent ? (
                                            <button
                                                onClick={handleSendOtp}
                                                disabled={otpSending || !phone.trim()}
                                                style={{ padding: '8px 16px', background: '#d97706', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                                            >
                                                {otpSending ? 'Sending…' : 'Send verification code'}
                                            </button>
                                        ) : (
                                            <div>
                                                <p style={{ fontSize: '12px', color: '#92400e', marginBottom: '8px' }}>
                                                    We sent a code to your WhatsApp. Tap the Copy Code button there, or type it in below.
                                                </p>
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={6}
                                                        value={otpCode}
                                                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                                        placeholder="123456"
                                                        style={{ width: '120px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', letterSpacing: '2px' }}
                                                    />
                                                    <button
                                                        onClick={handleVerifyOtp}
                                                        disabled={otpVerifying || otpCode.length !== 6}
                                                        style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                                                    >
                                                        {otpVerifying ? 'Verifying…' : 'Verify'}
                                                    </button>
                                                    <button
                                                        onClick={handleSendOtp}
                                                        disabled={otpSending || otpCooldown > 0}
                                                        style={{ padding: '8px 16px', background: 'transparent', color: '#92400e', border: '1px solid #fde68a', borderRadius: '6px', cursor: otpCooldown > 0 ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '13px' }}
                                                    >
                                                        {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend code'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {otpError && <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '8px' }}>{otpError}</p>}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Email Address</label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                />
                                {user?.emailVerified && <p style={{ fontSize: '11px', color: '#059669', marginTop: '4px' }}>✓ Email verified</p>}
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Current Password (to save changes)</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    placeholder="Required for email changes"
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={handleUpdateProfile}
                                    disabled={loading}
                                    style={{ padding: '10px 24px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                                >
                                    Save Profile Changes
                                </button>
                                <button
                                    onClick={handleChangeEmail}
                                    disabled={loading || !currentPassword}
                                    style={{ padding: '10px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                                >
                                    Change Email
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>Security Settings</h2>

                            {/* Change Password */}
                            <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Change Password</h3>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Current Password</label>
                                    <input
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>New Password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Confirm New Password</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                    />
                                </div>

                                <button
                                    onClick={handleChangePassword}
                                    disabled={loading || !currentPassword || !newPassword}
                                    style={{ padding: '10px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                                >
                                    Update Password
                                </button>
                            </div>

                            {/* Delete Account */}
                            <div>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#dc2626' }}>Delete Account</h3>
                                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                                    Once you delete your account, there is no going back. This will permanently delete:
                                </p>
                                <ul style={{ marginBottom: '16px', paddingLeft: '20px', fontSize: '13px', color: '#6b7280' }}>
                                    <li>All your bots and their configurations</li>
                                    <li>All conversation history and analytics</li>
                                    <li>Your billing information and invoices</li>
                                    <li>Your account credentials</li>
                                </ul>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    style={{ padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                                >
                                    Delete My Account
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Company Tab */}
                    {activeTab === 'company' && (
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '6px' }}>Company Details</h2>
                            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '24px' }}>This information appears on invoices and is used for business communications.</p>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>Company / Business Name</label>
                                    <input
                                        type="text"
                                        value={coName}
                                        onChange={(e) => setCoName(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                                        placeholder="Acme Corp"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>Industry</label>
                                    <select
                                        value={coIndustry}
                                        onChange={(e) => setCoIndustry(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' }}
                                    >
                                        <option value="">Select industry…</option>
                                        {['Retail', 'Food & Beverage', 'Healthcare', 'Real Estate', 'Education', 'Finance', 'Transport & Logistics', 'Hospitality', 'Agriculture', 'Construction', 'Technology', 'Media & Entertainment', 'Professional Services', 'Other'].map(i => (
                                            <option key={i} value={i}>{i}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>Website</label>
                                <input
                                    type="url"
                                    value={coWebsite}
                                    onChange={(e) => setCoWebsite(e.target.value)}
                                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                                    placeholder="https://yourcompany.com"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>Business Email</label>
                                    <input
                                        type="email"
                                        value={coEmail}
                                        onChange={(e) => setCoEmail(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                                        placeholder="info@company.com"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>Business Phone</label>
                                    <input
                                        type="tel"
                                        value={coPhone}
                                        onChange={(e) => setCoPhone(e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                                        placeholder="+263712345678"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleUpdateCompany}
                                disabled={loading}
                                style={{ padding: '10px 24px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                            >
                                Save Company Details
                            </button>
                        </div>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px' }}>Notification Preferences</h2>

                            <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-element)', borderRadius: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={emailNotifications}
                                        onChange={(e) => setEmailNotifications(e.target.checked)}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500' }}>📧 Email Notifications</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Receive important updates about your account and bots</div>
                                    </div>
                                </label>
                            </div>

                            <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-element)', borderRadius: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={smsNotifications}
                                        onChange={(e) => setSmsNotifications(e.target.checked)}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500' }}>📱 SMS Notifications</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Get text messages for important alerts</div>
                                    </div>
                                </label>
                            </div>

                            <div style={{ marginBottom: '24px', padding: '12px', background: 'var(--bg-element)', borderRadius: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={marketingEmails}
                                        onChange={(e) => setMarketingEmails(e.target.checked)}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: '500' }}>📨 Marketing Emails</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>Receive tips, updates, and special offers</div>
                                    </div>
                                </label>
                            </div>

                            <button
                                onClick={handleUpdateNotifications}
                                disabled={loading}
                                style={{ padding: '10px 24px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                            >
                                Save Notification Preferences
                            </button>

                            {/* ── Push Notifications ── */}
                            <div style={{ marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                                    <div>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>🔔 Browser Push Notifications</h3>
                                        <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                                            Get real-time alerts in this browser even when the tab is in the background.
                                        </p>
                                    </div>
                                    {push.supported ? (
                                        <button
                                            onClick={push.subscribed ? push.unsubscribe : push.subscribe}
                                            disabled={push.loading || push.permission === 'denied'}
                                            style={{
                                                padding: '8px 18px',
                                                borderRadius: 8,
                                                border: 'none',
                                                background: push.subscribed ? '#fee2e2' : '#5B4FE8',
                                                color: push.subscribed ? '#dc2626' : '#fff',
                                                cursor: push.loading || push.permission === 'denied' ? 'not-allowed' : 'pointer',
                                                fontWeight: 600,
                                                fontSize: 13,
                                                opacity: push.loading ? 0.7 : 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {push.loading ? '…' : push.subscribed ? 'Disable' : push.permission === 'denied' ? 'Blocked by browser' : 'Enable'}
                                        </button>
                                    ) : (
                                        <span style={{ fontSize: 12, color: '#9ca3af' }}>Not supported in this browser</span>
                                    )}
                                </div>

                                {push.permission === 'denied' && (
                                    <div style={{ padding: '10px 14px', background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 16 }}>
                                        Push notifications are blocked. To enable them, click the lock icon in your browser's address bar and allow notifications.
                                    </div>
                                )}

                                {push.subscribed && push.events.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: 0 }}>Notify me when:</p>
                                        {push.events.map(ev => (
                                            <label key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-element)', borderRadius: 8, cursor: 'pointer' }}>
                                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={push.preferences[ev.id] !== false}
                                                        onChange={e => push.updatePreference(ev.id, e.target.checked)}
                                                        style={{ width: 17, height: 17, accentColor: '#5B4FE8' }}
                                                    />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 500, fontSize: 14 }}>{ev.label}</div>
                                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{ev.desc}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Integrations Tab */}
                    {activeTab === 'integrations' && (
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Integrations</h2>
                            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
                                Generate a key here and paste it into a partner app (like TIRONGE) so it can create bots on your behalf —
                                e.g. a WhatsApp bot for one of your events.
                            </p>

                            {generatedIntegrationKey && (
                                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: '#065f46' }}>
                                        Copy this key now — it won't be shown again
                                    </div>
                                    <code style={{ display: 'block', background: 'white', padding: '10px 12px', borderRadius: '8px', fontSize: 13, wordBreak: 'break-all', border: '1px solid #d1fae5' }}>
                                        {generatedIntegrationKey}
                                    </code>
                                    <button
                                        onClick={() => setGeneratedIntegrationKey(null)}
                                        style={{ marginTop: 10, background: 'transparent', border: 'none', color: '#059669', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                                    >
                                        Done
                                    </button>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
                                <input
                                    value={newIntegrationLabel}
                                    onChange={e => setNewIntegrationLabel(e.target.value)}
                                    placeholder="Label (e.g. TIRONGE)"
                                    style={{ flex: '1 1 200px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: 14 }}
                                />
                                <button
                                    onClick={handleGenerateIntegrationKey}
                                    style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#059669', color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                                >
                                    Generate key
                                </button>
                            </div>

                            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#374151' }}>Active keys</h3>
                            {integrationsLoading ? (
                                <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
                            ) : integrationKeys.length === 0 ? (
                                <div style={{ color: '#6b7280', fontSize: 14 }}>No integration keys yet.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {integrationKeys.map(k => (
                                        <div key={k.hash} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: 14 }}>{k.label}</div>
                                                <div style={{ fontSize: 12, color: '#9ca3af' }}>Created {new Date(k.createdAt).toLocaleDateString()}</div>
                                            </div>
                                            <button
                                                onClick={() => handleRevokeIntegrationKey(k.hash)}
                                                style={{ background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '6px', padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}
                                            >
                                                Revoke
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '28px',
                        maxWidth: '480px',
                        width: '90%'
                    }}>
                        <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '16px' }}>⚠️</div>
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center', marginBottom: '8px' }}>Delete Account</h2>
                        <p style={{ color: '#6b7280', textAlign: 'center', marginBottom: '24px' }}>
                            This action cannot be undone. All your data will be permanently deleted.
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                                Type <span style={{ color: '#dc2626' }}>DELETE MY ACCOUNT</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                placeholder="DELETE MY ACCOUNT"
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>Enter your password</label>
                            <input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                                placeholder="Your current password"
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{ flex: 1, padding: '10px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || !currentPassword}
                                style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: deleteConfirmText !== 'DELETE MY ACCOUNT' || !currentPassword ? 0.5 : 1 }}
                            >
                                Permanently Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

</>

    );
}